import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  TablePagination,
  Skeleton,
} from "@mui/material";
import {
  descriptionCellStyle,
  likesViewsDateCommentsCellStyle,
  videoCellStyle,
  videoListTableStyle,
} from "./css";
import { getReadableDateString } from "../../utils/commons";
import { getVideoList } from "../../services/ContentServices";
import { Video } from "./types";
import { VideoThumbCard } from "../../components/ContentCard";

const rowsPerPageOptions = [5, 10, 25];
const VIDEO_ITEM_HEIGHT = 60;
const VIDEO_THUMB_WIDTH = Math.round((VIDEO_ITEM_HEIGHT * 16) / 9);

interface VideoTableProps {
  setSelectedVideo: React.Dispatch<React.SetStateAction<any>>;
  setShowViewVideo: React.Dispatch<React.SetStateAction<any>>;
}
const VideoTable: React.FC<VideoTableProps> = ({
  setSelectedVideo,
  setShowViewVideo,
}) => {
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageOptions[0]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const compactVideoCellStyle = {
    ...videoCellStyle,
    width: VIDEO_THUMB_WIDTH,
    maxWidth: VIDEO_THUMB_WIDTH,
    minWidth: VIDEO_THUMB_WIDTH,
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    getVideos();
  }, [page, rowsPerPage]);

  const getVideos = async () => {
    setLoading(true);
    try {
      const response = await getVideoList({ page, size: rowsPerPage });
      setVideos(response.data.content);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={videoListTableStyle}>
        <TableContainer sx={{ height: 80 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={compactVideoCellStyle}>Video</TableCell>
                <TableCell sx={descriptionCellStyle}></TableCell>
                <TableCell sx={likesViewsDateCommentsCellStyle}>
                  Likes
                </TableCell>
                <TableCell sx={likesViewsDateCommentsCellStyle}>
                  Views
                </TableCell>
                <TableCell sx={likesViewsDateCommentsCellStyle}>Date</TableCell>
                <TableCell sx={likesViewsDateCommentsCellStyle}>
                  Comments
                </TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>
        <Box
          sx={{
            height: "100%",
            overflowY: "auto",
            scrollbarWidth: "none",
          }}
        >
          {" "}
          <Box>
            <TableContainer>
              <Table>
                <TableBody>
                  {loading &&
                    Array.from({ length: rowsPerPage }).map((_, index) => (
                      <VideoTableRowSkeleton key={index} />
                    ))}

                  {!loading && videos.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No data found
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading &&
                    videos.map((video) => (
                      <TableRow
                        key={video.id}
                        onClick={() => {
                          setShowViewVideo(true);
                          setSelectedVideo(video.id);
                        }}
                      >
                        <TableCell
                          sx={{
                            ...compactVideoCellStyle,
                            cursor: "pointer",
                            py: 0.5,
                          }}
                        >
                          <VideoThumbCard
                            srcUrl={video.thumbnailUrl}
                            duration={video.duration}
                            width={"100%"}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            ...descriptionCellStyle,
                            cursor: "pointer",
                            py: 0.5,
                          }}
                        >
                          <Box>
                            <p
                              style={{
                                margin: 0,
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {video.title}
                            </p>
                            <p
                              style={{
                                wordBreak: "break-word",
                                margin: "4px 0 0",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {video.description.length > 150
                                ? `${video.description.slice(0, 150)}...`
                                : video.description}
                            </p>
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{ ...likesViewsDateCommentsCellStyle, py: 0.5 }}
                        >
                          {video.totalLikes}
                        </TableCell>
                        <TableCell
                          sx={{ ...likesViewsDateCommentsCellStyle, py: 0.5 }}
                        >
                          {video.totalViews}
                        </TableCell>
                        <TableCell
                          sx={{ ...likesViewsDateCommentsCellStyle, py: 0.5 }}
                        >
                          {getReadableDateString(video.createdAt)}
                        </TableCell>
                        <TableCell
                          sx={{ ...likesViewsDateCommentsCellStyle, py: 0.5 }}
                        >
                          {video.totalComments}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Box>
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={totalElements}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
};

export default VideoTable;

const VideoTableRowSkeleton = () => (
  <TableRow>
    <TableCell
      sx={{
        ...videoCellStyle,
        width: VIDEO_THUMB_WIDTH,
        maxWidth: VIDEO_THUMB_WIDTH,
        minWidth: VIDEO_THUMB_WIDTH,
        py: 0.5,
      }}
    >
      <Skeleton
        variant="rectangular"
        width={"100%"}
        height={VIDEO_ITEM_HEIGHT}
      />
    </TableCell>
    <TableCell sx={{ ...descriptionCellStyle, py: 0.5 }}>
      <Box>
        <Skeleton variant="text" width={150} height={20} />
        <Skeleton variant="text" width={300} height={40} />
      </Box>
    </TableCell>
    <TableCell sx={{ ...likesViewsDateCommentsCellStyle, py: 0.5 }}>
      <Skeleton variant="text" width={50} height={20} />
    </TableCell>
    <TableCell sx={{ ...likesViewsDateCommentsCellStyle, py: 0.5 }}>
      <Skeleton variant="text" width={50} height={20} />
    </TableCell>
    <TableCell sx={{ ...likesViewsDateCommentsCellStyle, py: 0.5 }}>
      <Skeleton variant="text" width={60} height={20} />
    </TableCell>
    <TableCell sx={{ ...likesViewsDateCommentsCellStyle, py: 0.5 }}>
      <Skeleton variant="text" width={50} height={20} />
    </TableCell>
  </TableRow>
);
